import { observer } from "mobx-react-lite";

import { AuditEventTypeSelect } from "./AuditEventTypeSelect";
import { useAuditFilterStore } from "./audit-filter-store-context";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface TimeRange {
  id: string;
  label: string;
  resolve: () => [string, string] | null;
}

const TIME_RANGES: TimeRange[] = [
  { id: "all", label: "All time", resolve: () => null },
  {
    id: "24h",
    label: "Last 24 hours",
    resolve: () => {
      const to = new Date();
      const from = new Date(to.getTime() - 24 * 60 * 60 * 1000);
      return [from.toISOString(), to.toISOString()];
    },
  },
  {
    id: "7d",
    label: "Last 7 days",
    resolve: () => {
      const to = new Date();
      const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
      return [from.toISOString(), to.toISOString()];
    },
  },
  {
    id: "30d",
    label: "Last 30 days",
    resolve: () => {
      const to = new Date();
      const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
      return [from.toISOString(), to.toISOString()];
    },
  },
];

const ANY_SOURCE = "__any__";

const SOURCES: { value: string; label: string }[] = [
  { value: ANY_SOURCE, label: "Any source" },
  { value: "sso", label: "SSO" },
  { value: "directory_sync", label: "Directory sync" },
  { value: "manual", label: "Manual (Owner)" },
  { value: "login", label: "Password login" },
  { value: "system", label: "System" },
];

/** Filter panel content for the audit log DataTable filter popover. */
const AuditFilterPanelInner = () => {
  const store = useAuditFilterStore();
  const { filters } = store;
  const selectedEventTypes = filters.event_types ?? [];

  const activeTimeRangeId = (() => {
    if (!filters.from && !filters.to) {
      return "all";
    }
    const matchFromTo = (id: string): boolean => {
      const range = TIME_RANGES.find(r => r.id === id);
      if (!range) {
        return false;
      }
      const resolved = range.resolve();
      if (!resolved) {
        return false;
      }
      const [from, to] = resolved;
      return (
        Math.abs(new Date(from).getTime() - new Date(filters.from ?? "").getTime()) < 60_000 &&
        Math.abs(new Date(to).getTime() - new Date(filters.to ?? "").getTime()) < 60_000
      );
    };
    return TIME_RANGES.find(r => r.id !== "all" && matchFromTo(r.id))?.id ?? "custom";
  })();

  const handleTimeRangeChange = (id: string) => {
    const range = TIME_RANGES.find(r => r.id === id);
    if (!range) {
      return;
    }
    const resolved = range.resolve();
    if (!resolved) {
      store.setTimeRange(null, null);
    } else {
      store.setTimeRange(resolved[0], resolved[1]);
    }
  };

  return (
    <div className="w-full">
      <div className="border-b border-gray-100 px-4 py-3">
        <p className="text-sm font-medium text-gray-900">Audit log filters</p>
        <p className="mt-0.5 text-xs text-gray-500">
          Filters sync to the URL so you can share a filtered view.
        </p>
      </div>

      <div className="space-y-4 px-4 py-3">
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700">Source</label>
          <Select
            value={filters.source ?? ANY_SOURCE}
            onValueChange={value => store.setSource(value === ANY_SOURCE ? null : value)}
          >
            <SelectTrigger className="h-9 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SOURCES.map(s => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700">Time range</label>
          <Select value={activeTimeRangeId} onValueChange={handleTimeRangeChange}>
            <SelectTrigger className="h-9 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_RANGES.map(r => (
                <SelectItem key={r.id} value={r.id}>
                  {r.label}
                </SelectItem>
              ))}
              {activeTimeRangeId === "custom" && (
                <SelectItem value="custom" disabled>
                  Custom range
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-gray-700">Event types</label>
            {selectedEventTypes.length > 0 && (
              <button
                type="button"
                className="text-xs text-gray-500 hover:text-gray-700"
                onClick={() => store.setEventTypes([])}
              >
                Clear
              </button>
            )}
          </div>
          <AuditEventTypeSelect
            selectedValues={selectedEventTypes}
            onValueChange={eventTypes => store.setEventTypes(eventTypes)}
            placeholder="All event types"
          />
        </div>
      </div>

      {store.isAnyActive && (
        <div className="border-t border-gray-100 px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => store.reset()}
            className="w-full text-gray-600"
          >
            Clear all filters
          </Button>
        </div>
      )}
    </div>
  );
};

export const AuditFilterPanel = observer(AuditFilterPanelInner);
